import React from "react";
import { Image, ArrowUpRight, ArrowDownRight } from "lucide-react";
import TransparentCard from "../TransparentCard";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../../utils/formatPrice";
import { translateSectorLabel } from "../../utils/sectorLabel";
import "../../css/model.css";

const ModelHeader = ({ modelData }) => {
  const { i18n } = useTranslation();
  if (!modelData) return null;

  const { name, description, price, currency, change, changeValue, date, regulatory, imageUrl } = modelData;
  const descriptionLines = description ? description.split('\n') : null;

  const hasChange = change !== null && change !== undefined;
  const isPositive = hasChange && change >= 0;
  const changePrefix = isPositive ? '+' : '';
  const ChangeIcon = isPositive ? ArrowUpRight : ArrowDownRight;

  return (
    <TransparentCard className="model-header">
      <div className="model-header__container">
        {/* Icon/Image Area */}
        <div className="model-header__icon">
          {imageUrl ? (
            <img src={imageUrl} alt={name} className="model-header__image" />
          ) : (
            <Image size={40} strokeWidth={1.5} color="rgba(255, 255, 255, 0.3)" />
          )}
        </div>

        {/* Content Area */}
        <div className="model-header__content">
          <h1 className="model-header__title">{name}</h1>
          {descriptionLines && (
            <p className="model-header__description">
              {descriptionLines.map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < descriptionLines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>

        {/* Price Area */}
        <div className="model-header__price-section">
          <div className="model-header__price">
            {formatPrice(price)} {currency}
          </div>

          {/* Date row: "Daily   Apr 23, 2025" style */}
          <div className="model-header__date">
            {regulatory && (
              <span className="model-header__period">{translateSectorLabel(regulatory, i18n.resolvedLanguage || i18n.language)}</span>
            )}
            <span>{date ?? '—'}</span>
          </div>

          {/* Change row */}
          <div className="model-header__change-row">
            <span className={`model-header__change-pct ${hasChange ? (isPositive ? 'positive' : 'negative') : ''}`}>
              {hasChange ? (
                <>
                  <ChangeIcon size={14} className="model-header__change-icon" />
                  {changePrefix}{change.toFixed(2)}%
                </>
              ) : (
                <span>—</span>
              )}
            </span>
            <span className="model-header__change-value">
              {changeValue != null ? (
                `/ ${changePrefix}${formatPrice(Math.abs(changeValue))} ${currency?.split('/')[0]}`
              ) : null}
            </span>
          </div>
        </div>
      </div>
    </TransparentCard>
  );
};

export default ModelHeader;
